import { MassApprovalRequest } from '../../../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalDetailsSlice'
import { MassApprovalFeedback, FailedFeedback } from '../../../../types/massApproval'
import {
	ErrorModalData,
	AvailabilityModalData,
	modalDataType,
	modalTitleType,
	ScheduleModalData,
	ScheduleTasksModalData,
	ActivityDeclarationsModalData,
	AssignSelectedModalData,
	SameChapaModalData,
	SendScheduleModalData,
	SubcontractDriversModalData,
	ServiceItineraryModalData,
	ServiceDetailModalData,
	VehicleWorkshopModalData,
	DriversWithoutRestModalData,
	DriversVehiclesAvailabilityModalData,
	RestAfterWorkModalData,
	TachographServiceTypeModalData,
	ChapasPorMotoristasModalData,
	NonScheduledDriversModalData,
	OvertimeModalData,
	TimesheetNoticesModalData,
	TimesheetMultipleDaysModalData,
	ScheduleEmailsModalData,
	VehicleServiceTotalsModalData,
	DriverServiceTotalsModalData,
	VehicleAccidentsModalData,
	DriverSituationsModalData,
	VehicleSituationsModalData,
	VehicleWashModalData,
	StatusMessageModalData,
	ServicesByClientModalData,
	ServicesByLocalityModalData,
	ItinerarySheetsModalData,
} from '../../../../types/modal'

/**
 * Validates the structure and content of `ErrorModalData`.
 *
 * @param data - The `ErrorModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateErrorModalData = (data: ErrorModalData): boolean => {
	return (
		'errorCode' in data &&
		data.errorCode !== '' &&
		'errorMessage' in data &&
		data.errorMessage !== '' &&
		'exceptionMessage' in data &&
		data.exceptionMessage !== '' &&
		'exceptionSource' in data &&
		data.exceptionSource !== '' &&
		'stackTrace' in data &&
		data.stackTrace !== ''
	)
}

/**
 * Validates the structure and content of `AvailabilityModalData`.
 *
 * @param data - The `AvailabilityModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateAvailabilityModalData = (data: AvailabilityModalData): boolean => {
	return (
		'workedSchedule' in data &&
		Array.isArray(data.workedSchedule) &&
		'totalsByPeriod' in data &&
		data.totalsByPeriod !== null &&
		'totalAvailability' in data &&
		data.totalAvailability !== '' &&
		'totalAvailabilityFormatted' in data &&
		data.totalAvailabilityFormatted !== '' &&
		'overtime' in data &&
		data.overtime !== '' &&
		'drivingBreak' in data
	)
}

/**
 * Validates the structure and content of `ScheduleModalData`.
 *
 * @param data - The `ScheduleModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateScheduleModalData = (data: ScheduleModalData): boolean => {
	return (
		'centroId' in data &&
		typeof data.centroId === 'string' &&
		data.centroId.trim() !== '' &&
		'date' in data &&
		typeof data.date === 'string' &&
		data.date.trim() !== '' &&
		'filters' in data &&
		data.filters !== null &&
		typeof data.filters === 'object'
	)
}

/**
 * Validates the structure and content of `ScheduleTasksModalData`.
 *
 * @param data - The `ScheduleTasksModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateScheduleTasksModalData = (data: ScheduleTasksModalData): boolean => {
	return (
		'centroId' in data &&
		typeof data.centroId === 'string' &&
		data.centroId.trim() !== '' &&
		'date' in data &&
		typeof data.date === 'string' &&
		data.date.trim() !== '' &&
		'tasks' in data &&
		Array.isArray(data.tasks)
	)
}

/**
 * Validates the structure and content of `ActivityDeclarationsModalData`.
 *
 * @param data - The `ActivityDeclarationsModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateActivityDeclarationsModalData = (data: ActivityDeclarationsModalData): boolean => {
	return 'toIssue' in data && Array.isArray(data.toIssue) && 'issued' in data && Array.isArray(data.issued)
}

/**
 * Validates the structure and content of `SendScheduleModalData`.
 *
 * @param data - The `SendScheduleModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateSendScheduleModalData = (data: SendScheduleModalData): boolean => {
	return (
		'centroId' in data &&
		typeof data.centroId === 'string' &&
		data.centroId.trim() !== '' &&
		'date' in data &&
		typeof data.date === 'string' &&
		data.date.trim() !== '' &&
		'scheduledServices' in data &&
		Array.isArray(data.scheduledServices)
	)
}

/**
 * Validates the structure and content of `SubcontractDriversModalData`.
 *
 * @param data - The `SubcontractDriversModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateSubcontractDriversModalData = (data: SubcontractDriversModalData): boolean => {
	return 'services' in data && Array.isArray(data.services)
}

/**
 * Validates the structure and content of `ServiceItineraryModalData`.
 *
 * @param data - The `ServiceItineraryModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateServiceItineraryModalData = (data: ServiceItineraryModalData): boolean => {
	return (
		'serviceId' in data &&
		(typeof data.serviceId === 'string' || typeof data.serviceId === 'number') &&
		String(data.serviceId).trim() !== ''
	)
}

/**
 * Validates the structure and content of `ServiceDetailModalData`.
 *
 * @param data - The `ServiceDetailModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateServiceDetailModalData = (data: ServiceDetailModalData): boolean => {
	return (
		'serviceId' in data &&
		(typeof data.serviceId === 'string' || typeof data.serviceId === 'number') &&
		String(data.serviceId).trim() !== ''
	)
}

/**
 * Validates the structure and content of `AssignSelectedModalData`.
 *
 * @param data - The `AssignSelectedModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateAssignSelectedModalData = (data: AssignSelectedModalData): boolean => {
	return (
		'driverName' in data &&
		typeof data.driverName === 'string' &&
		data.driverName.trim() !== '' &&
		'selectedCount' in data &&
		typeof data.selectedCount === 'number' &&
		data.selectedCount >= 0
	)
}

/**
 * Validates the structure and content of `SameChapaModalData`.
 *
 * @param data - The `SameChapaModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateSameChapaModalData = (data: SameChapaModalData): boolean => {
	return (
		'serviceId' in data &&
		typeof data.serviceId === 'string' &&
		data.serviceId.trim() !== '' &&
		'chapa' in data &&
		typeof data.chapa === 'string' &&
		data.chapa.trim() !== ''
	)
}

/**
 * Validates the structure and content of `VehicleWorkshopModalData`.
 *
 * @param data - The `VehicleWorkshopModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateVehicleWorkshopModalData = (data: VehicleWorkshopModalData): boolean => {
	return (
		'vehicleCode' in data &&
		typeof data.vehicleCode === 'string' &&
		data.vehicleCode.trim() !== '' &&
		'servicesCount' in data &&
		typeof data.servicesCount === 'number' &&
		data.servicesCount >= 0
	)
}

/**
 * Validates the structure and content of `StatusMessageModalData`.
 *
 * @param data - The `StatusMessageModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateStatusMessageModalData = (data: StatusMessageModalData): boolean => {
	return 'message' in data && typeof data.message === 'string' && data.message.trim() !== ''
}

/**
 * Validates the structure and content of `DriversWithoutRestModalData`.
 *
 * @param data - The `DriversWithoutRestModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateDriversWithoutRestModalData = (data: DriversWithoutRestModalData): boolean => {
	return 'rows' in data && Array.isArray(data.rows)
}

/**
 * Validates the structure and content of `RestAfterWorkModalData`.
 *
 * @param data - The `RestAfterWorkModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateRestAfterWorkModalData = (data: RestAfterWorkModalData): boolean => {
	return 'rows' in data && Array.isArray(data.rows)
}

/**
 * Validates the structure and content of `DriversVehiclesAvailabilityModalData`.
 *
 * @param data - The `DriversVehiclesAvailabilityModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateDriversVehiclesAvailabilityModalData = (data: DriversVehiclesAvailabilityModalData): boolean => {
	return 'drivers' in data && Array.isArray(data.drivers) && 'vehicles' in data && Array.isArray(data.vehicles)
}

/**
 * Validates the structure and content of `TachographServiceTypeModalData`.
 *
 * @param data - The `TachographServiceTypeModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateTachographServiceTypeModalData = (data: TachographServiceTypeModalData): boolean => {
	return 'rows' in data && Array.isArray(data.rows)
}

/**
 * Validates the structure and content of `ChapasPorMotoristasModalData`.
 *
 * @param data - The `ChapasPorMotoristasModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateChapasPorMotoristasModalData = (data: ChapasPorMotoristasModalData): boolean => {
	return 'rows' in data && Array.isArray(data.rows)
}

const validateNonScheduledDriversModalData = (data: NonScheduledDriversModalData): boolean => {
	return data !== null && typeof data === 'object'
}

/**
 * Validates the structure and content of `OvertimeModalData`.
 *
 * @param data - The `OvertimeModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateOvertimeModalData = (data: OvertimeModalData): boolean => {
	return 'rows' in data && Array.isArray(data.rows)
}

/**
 * Validates the structure and content of `TimesheetNoticesModalData`.
 *
 * @param data - The `TimesheetNoticesModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateTimesheetNoticesModalData = (data: TimesheetNoticesModalData): boolean => {
	return (
		'timesheets' in data && Array.isArray(data.timesheets) && 'lateWarnings' in data && Array.isArray(data.lateWarnings)
	)
}

/**
 * Validates the structure and content of `TimesheetMultipleDaysModalData`.
 *
 * @param data - The `TimesheetMultipleDaysModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateTimesheetMultipleDaysModalData = (data: TimesheetMultipleDaysModalData): boolean => {
	return 'drivers' in data && Array.isArray(data.drivers) && 'days' in data && Array.isArray(data.days)
}

/**
 * Validates the structure and content of `ScheduleEmailsModalData`.
 *
 * @param data - The `ScheduleEmailsModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateScheduleEmailsModalData = (data: ScheduleEmailsModalData): boolean => {
	return (
		'recipients' in data && Array.isArray(data.recipients) && 'ccRecipients' in data && Array.isArray(data.ccRecipients)
	)
}

/**
 * Validates the structure and content of `ItinerarySheetsModalData`.
 *
 * @param data - The `ItinerarySheetsModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateItinerarySheetsModalData = (data: ItinerarySheetsModalData): boolean => {
	return 'rows' in data && Array.isArray(data.rows)
}

/**
 * Validates the structure and content of `ServicesByClientModalData`.
 *
 * @param data - The `ServicesByClientModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateServicesByClientModalData = (data: ServicesByClientModalData): boolean => {
	return 'rows' in data && Array.isArray(data.rows)
}

/**
 * Validates the structure and content of `ServicesByLocalityModalData`.
 *
 * @param data - The `ServicesByLocalityModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateServicesByLocalityModalData = (data: ServicesByLocalityModalData): boolean => {
	return 'rows' in data && Array.isArray(data.rows)
}

/**
 * Validates the structure and content of `VehicleServiceTotalsModalData`.
 *
 * @param data - The `VehicleServiceTotalsModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateVehicleServiceTotalsModalData = (data: VehicleServiceTotalsModalData): boolean => {
	return 'services' in data && Array.isArray(data.services) && 'totals' in data && Array.isArray(data.totals)
}

/**
 * Validates the structure and content of `DriverServiceTotalsModalData`.
 *
 * @param data - The `DriverServiceTotalsModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateDriverServiceTotalsModalData = (data: DriverServiceTotalsModalData): boolean => {
	return 'services' in data && Array.isArray(data.services) && 'totals' in data && Array.isArray(data.totals)
}

/**
 * Validates the structure and content of `VehicleAccidentsModalData`.
 *
 * @param data - The `VehicleAccidentsModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateVehicleAccidentsModalData = (data: VehicleAccidentsModalData): boolean => {
	return 'rows' in data && Array.isArray(data.rows)
}

/**
 * Validates the structure and content of `DriverSituationsModalData`.
 *
 * @param data - The `DriverSituationsModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateDriverSituationsModalData = (data: DriverSituationsModalData): boolean => {
	return 'rows' in data && Array.isArray(data.rows)
}

/**
 * Validates the structure and content of `VehicleSituationsModalData`.
 *
 * @param data - The `VehicleSituationsModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateVehicleSituationsModalData = (data: VehicleSituationsModalData): boolean => {
	return 'rows' in data && Array.isArray(data.rows)
}

/**
 * Validates the structure and content of `VehicleWashModalData`.
 *
 * @param data - The `VehicleWashModalData` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
const validateVehicleWashModalData = (data: VehicleWashModalData): boolean => {
	return 'rows' in data && Array.isArray(data.rows)
}

// TODO: Fix validation logic
/**
 * Validates the structure and content of `FailedFeedback`.
 *
 * @param data - The `FailedFeedback` object to validate.
 * @returns `true` if the data is valid, otherwise `false`.
 */
// const validateMassApprovalRetryModalData = (
//   data: FailedFeedback,
// ): boolean => {
//   return (
//     'failed' in data &&
//     Array.isArray(data.failed) &&
//     'approved' in data &&
//     Array.isArray(data.approved)
//   );
// };

/**
 * Validates the structure and content of `MassApprovalRequest` array.
 *
 * @param data - The array of `MassApprovalRequest` objects to validate.
 * @returns `true` if all items in the array are valid, otherwise `false`.
 */
const validateMassApprovalConfirmationModalData = (data: MassApprovalRequest[]): boolean => {
	return (
		Array.isArray(data) &&
		data.every(item => {
			return item.status === 'SubmittedWithoutChanges'
		})
	)
}

/**
 * Custom hook for validating modal data based on the modal title.
 *
 * @param modalTitle - The title of the modal to determine the validation logic.
 * @param data - The data to validate, which can be of various types depending on the modal.
 * @returns `true` if the data is valid for the given modal title, otherwise `false`.
 */
export const useCustomModalValidation = (
	modalTitle: modalTitleType,
	data: modalDataType | AvailabilityModalData | FailedFeedback | MassApprovalFeedback | MassApprovalRequest[],
) => {
	if (modalTitle === 'Error Details') {
		return validateErrorModalData(data as ErrorModalData)
	}
	if (modalTitle === 'Disponibilidade e Pausas') {
		return validateAvailabilityModalData(data as AvailabilityModalData)
	}
	if (modalTitle === 'Gerir Escala') {
		return validateScheduleModalData(data as ScheduleModalData)
	}
	if (modalTitle === 'Tarefas da Escala') {
		return validateScheduleTasksModalData(data as ScheduleTasksModalData)
	}
	if (modalTitle === 'Declaracoes de Atividade') {
		return validateActivityDeclarationsModalData(data as ActivityDeclarationsModalData)
	}
	if (modalTitle === 'Enviar Escala') {
		return validateSendScheduleModalData(data as SendScheduleModalData)
	}
	if (modalTitle === 'Subcontratar Motoristas') {
		return validateSubcontractDriversModalData(data as SubcontractDriversModalData)
	}
	if (modalTitle === 'Requisicao do Servico') {
		return validateServiceItineraryModalData(data as ServiceItineraryModalData)
	}
	if (modalTitle === 'Detalhe do Servico') {
		return validateServiceDetailModalData(data as ServiceDetailModalData)
	}
	if (modalTitle === 'Atribuir Selecionados') {
		return validateAssignSelectedModalData(data as AssignSelectedModalData)
	}
	if (modalTitle === 'Atribuicao Concluida') {
		return validateAssignSelectedModalData(data as AssignSelectedModalData)
	}
	if (modalTitle === 'Viatura em Oficina') {
		return validateVehicleWorkshopModalData(data as VehicleWorkshopModalData)
	}
	if (modalTitle === 'Motoristas sem folga ha mais de 6 dias') {
		return validateDriversWithoutRestModalData(data as DriversWithoutRestModalData)
	}
	if (modalTitle === 'Descansos ap\u00f3s Folga / Complementar Trabalhada') {
		return validateRestAfterWorkModalData(data as RestAfterWorkModalData)
	}
	if (modalTitle === 'Disponibilidade de Motoristas e Viaturas') {
		return validateDriversVehiclesAvailabilityModalData(data as DriversVehiclesAvailabilityModalData)
	}
	if (modalTitle === 'Servi\u00e7os por Tipo de Tac\u00f3grafo') {
		return validateTachographServiceTypeModalData(data as TachographServiceTypeModalData)
	}
	if (modalTitle === 'Chapas por Motoristas') {
		return validateChapasPorMotoristasModalData(data as ChapasPorMotoristasModalData)
	}
	if (modalTitle === 'Alterar Motoristas n\u00e3o Escalados') {
		return validateNonScheduledDriversModalData(data as NonScheduledDriversModalData)
	}
	if (modalTitle === 'Horas Extra') {
		return validateOvertimeModalData(data as OvertimeModalData)
	}
	if (modalTitle === 'Folhas de Ponto e Avisos de Folga Atrasada') {
		return validateTimesheetNoticesModalData(data as TimesheetNoticesModalData)
	}
	if (modalTitle === 'Folhas de Ponto de v\u00e1rios dias') {
		return validateTimesheetMultipleDaysModalData(data as TimesheetMultipleDaysModalData)
	}
	if (modalTitle === 'Emails que recebem a escala') {
		return validateScheduleEmailsModalData(data as ScheduleEmailsModalData)
	}
	if (modalTitle === 'Folhas de Itiner\u00e1rio') {
		return validateItinerarySheetsModalData(data as ItinerarySheetsModalData)
	}
	if (modalTitle === 'Servicos por Cliente') {
		return validateServicesByClientModalData(data as ServicesByClientModalData)
	}
	if (modalTitle === 'Servi\u00e7os por Localidade') {
		return validateServicesByLocalityModalData(data as ServicesByLocalityModalData)
	}
	if (modalTitle === 'Servicos e Totais por Viatura') {
		return validateVehicleServiceTotalsModalData(data as VehicleServiceTotalsModalData)
	}
	if (modalTitle === 'Servicos e Totais por Motorista') {
		return validateDriverServiceTotalsModalData(data as DriverServiceTotalsModalData)
	}
	if (modalTitle === 'Situa\u00e7\u00f5es do Motorista') {
		return validateDriverSituationsModalData(data as DriverSituationsModalData)
	}
	if (modalTitle === 'Situa\u00e7\u00f5es da Viatura') {
		return validateVehicleSituationsModalData(data as VehicleSituationsModalData)
	}
	if (modalTitle === 'Sinistralidade da Viatura') {
		return validateVehicleAccidentsModalData(data as VehicleAccidentsModalData)
	}
	if (modalTitle === 'Lavagem de Viaturas') {
		return validateVehicleWashModalData(data as VehicleWashModalData)
	}
	if (modalTitle === 'Mensagem Informativa' || modalTitle === 'Escala Enviada' || modalTitle === 'Mensagem de Erro') {
		return validateStatusMessageModalData(data as StatusMessageModalData)
	}
	if (modalTitle === 'Escalar outros servi\u00e7os da mesma chapa / requisi\u00e7\u00e3o') {
		return validateSameChapaModalData(data as SameChapaModalData)
	}
	// TODO: Fix validMassApprovalModalData
	// if (modalTitle === 'Rever Folhas de Ponto Submetidas sem Altera\u00e7\u00f5es') {
	//   return validateMassApprovalRetryModalData(
	//     data as FailedFeedback,
	//   );
	// }
	if (modalTitle === 'Aprovar Folhas de Ponto sem altera\u00e7\u00f5es') {
		return validateMassApprovalConfirmationModalData(data as MassApprovalRequest[])
	}
	return false
}
